import { useEffect, useState } from "react";
import { ApiError, api } from "@/lib/api";
import Toggle from "@/ui/Toggle";
import { useToast } from "@/ui/Toast";

export type JimiCuenta = {
  id: number;
  nombre: string;
  jimi_user_id: number | null;
  activo: boolean;
  nota: string | null;
};

/**
 * Las cuentas del portal de Jimi que son NUESTRAS.
 *
 * Jimi firma cada mensaje del hilo con el nombre de quien lo escribió. Con esta
 * lista, ArkTickets sabe cuándo un mensaje lo puso el equipo (desde el portal o
 * desde el caso) y cuándo lo puso de verdad el técnico de Jimi — así no avisa
 * "Jimi contestó" por algo que escribimos nosotros.
 *
 * Se PAUSAN en vez de borrarse: los mensajes viejos de esa cuenta se siguen
 * atribuyendo bien.
 */
export default function JimiCuentasPanel() {
  const toast = useToast();
  const [cuentas, setCuentas] = useState<JimiCuenta[]>([]);
  const [cargando, setCargando] = useState(true);
  const [nombre, setNombre] = useState("");
  const [userId, setUserId] = useState("");
  const [nota, setNota] = useState("");
  const [guardando, setGuardando] = useState(false);

  async function cargar() {
    try {
      setCuentas(await api.get<JimiCuenta[]>("/api/jimi-cuentas"));
    } catch {
      // sin permiso o red caída: la sección se queda vacía
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    void cargar();
  }, []);

  async function agregar() {
    const n = nombre.trim();
    if (!n || guardando) return;
    setGuardando(true);
    try {
      await api.post<JimiCuenta>("/api/jimi-cuentas", {
        nombre: n,
        jimi_user_id: userId.trim() ? Number(userId) : null,
        nota: nota.trim() || null,
        activo: true,
      });
      setNombre("");
      setUserId("");
      setNota("");
      toast.success("Cuenta agregada", n);
      await cargar();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude agregarla", msg);
    } finally {
      setGuardando(false);
    }
  }

  async function pausar(c: JimiCuenta, activo: boolean) {
    setCuentas((prev) => prev.map((x) => (x.id === c.id ? { ...x, activo } : x)));
    try {
      await api.patch(`/api/jimi-cuentas/${c.id}`, { ...c, activo });
      toast.success(activo ? "Cuenta activa" : "Cuenta en pausa", c.nombre);
    } catch (err) {
      setCuentas((prev) => prev.map((x) => (x.id === c.id ? { ...x, activo: !activo } : x)));
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cambiarla", msg);
    }
  }

  return (
    <div className="border-t border-ink-700 pt-3 space-y-2">
      <div>
        <h3 className="text-sm font-medium text-zinc-300">
          Nuestras cuentas en el portal de Jimi
        </h3>
        <p className="text-xs text-zinc-500 mt-1">
          Jimi firma cada mensaje del ticket con el nombre de quien lo escribió.
          Con esta lista el sistema sabe cuándo el mensaje lo puso{" "}
          <strong>nuestro equipo</strong> —desde el portal o desde el caso— y
          cuándo lo puso de verdad el técnico de Jimi, para no avisarte "Jimi
          contestó" por algo que escribiste tú. Si abren otra cuenta en Jimi,
          agrégala aquí.
        </p>
      </div>

      {cargando ? (
        <p className="text-xs text-zinc-500">Cargando…</p>
      ) : cuentas.length === 0 ? (
        <p className="text-xs text-zinc-500">
          No hay cuentas dadas de alta: todos los mensajes se van a tomar como
          del técnico de Jimi.
        </p>
      ) : (
        <ul className="divide-y divide-ink-700 rounded border border-ink-700">
          {cuentas.map((c) => (
            <li
              key={c.id}
              className="flex flex-col gap-2 p-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="text-sm text-zinc-200 truncate">
                  {c.nombre}
                  {c.jimi_user_id && (
                    <span className="text-[11px] text-zinc-500 font-mono">
                      {" "}
                      · id {c.jimi_user_id}
                    </span>
                  )}
                </p>
                {c.nota && <p className="text-[11px] text-zinc-500 truncate">{c.nota}</p>}
              </div>
              <Toggle
                checked={c.activo}
                onChange={(v) => void pausar(c, v)}
                label={c.activo ? "Activa" : "En pausa"}
              />
            </li>
          ))}
        </ul>
      )}

      {/* Siempre en columna: esto vive en la columna angosta (~320px) del panel
          de Configuración, donde una fila deja los campos ilegibles. */}
      <div className="flex flex-col gap-2">
        <label className="text-xs text-zinc-400">
          Nombre en Jimi
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Emprenet"
            className="input w-full mt-1"
          />
          <span className="mt-1 block text-[11px] text-zinc-500">
            Tal cual aparece firmando los mensajes en el portal de Jimi.
          </span>
        </label>
        <label className="text-xs text-zinc-400">
          ID interno
          <input
            value={userId}
            onChange={(e) => setUserId(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            placeholder="974"
            className="input w-full mt-1"
          />
          <span className="mt-1 block text-[11px] text-zinc-500">
            Opcional, pero más estable que el nombre: si en Jimi renombran la cuenta, el id no cambia.
          </span>
        </label>
        <label className="text-xs text-zinc-400">
          Nota
          <input
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="De quién es esta cuenta"
            className="input w-full mt-1"
          />
        </label>
        <button
          type="button"
          onClick={() => void agregar()}
          disabled={!nombre.trim() || guardando}
          className="btn-primary text-xs w-full disabled:opacity-50"
        >
          Agregar
        </button>
      </div>
    </div>
  );
}
